import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';

import { useAuth, useAuthActions } from '@/store/authStore';
import LoginForm from '@/components/auth/LoginForm';
import Layout from '@/components/layout/Layout';
import Dashboard from '@/pages/Dashboard';
import BookingList from '@/pages/BookingList';
import BookingDetail from '@/pages/BookingDetail';
import CreateBooking from '@/pages/CreateBooking';
import Analytics from '@/pages/Analytics';
import Settings from '@/pages/Settings';
import StaffManagement from '@/pages/StaffManagement';
import CourtManagement from '@/pages/CourtManagement';
import CustomerManagement from '@/pages/CustomerManagement';
import ContentManagement from '@/pages/ContentManagement';
import CourseScheduler from '@/pages/CourseScheduler';
import LoadingScreen from '@/components/ui/LoadingScreen';

// 创建React Query客户端
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5分钟
      retry: (failureCount, error: unknown) => {
        // 认证错误不重试
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
          (error as { response?: { status?: number } }).response?.status === 401
        ) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// 路由保护组件
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// 登录路由组件
const LoginRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <LoginForm />;
};

const App: React.FC = () => {
  const { loading } = useAuth();
  const { refreshAuth } = useAuthActions();

  // 应用初始化
  useEffect(() => {
    // 尝试刷新认证状态
    refreshAuth();
  }, [refreshAuth]);

  // 显示加载屏幕
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <AnimatePresence mode="wait">
            <Routes>
              {/* 登录路由 */}
              <Route path="/login" element={<LoginRoute />} />
              
              {/* 受保护的路由 */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/bookings" element={
                <ProtectedRoute>
                  <Layout>
                    <BookingList />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/bookings/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <BookingDetail />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/bookings/create" element={
                <ProtectedRoute>
                  <Layout>
                    <CreateBooking />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } />
              

              <Route path="/staff" element={
                <ProtectedRoute>
                  <Layout>
                    <StaffManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/courts" element={
                <ProtectedRoute>
                  <Layout>
                    <CourtManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/customers" element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/content" element={
                <ProtectedRoute>
                  <Layout>
                    <ContentManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/programme" element={
                <ProtectedRoute>
                  <Layout>
                    <CourseScheduler />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/scheduling" element={<Navigate to="/programme" replace />} />

              <Route path="/programme-content" element={<Navigate to="/programme?view=profiles" replace />} />

              <Route path="/campaigns" element={<Navigate to="/programme?view=promotion" replace />} />
              
              {/* 404重定向 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AnimatePresence>

          {/* 全局通知组件 */}
          <Toaster
            position="top-right"
            gutter={8}
            containerClassName="!top-20 !right-4"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.04)',
                fontSize: '14px',
                fontWeight: '500',
                padding: '16px',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
              loading: {
                iconTheme: {
                  primary: '#0ea5e9',
                  secondary: '#ffffff',
                },
              },
            }}
          />

        </div>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
